import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Award, Compass } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";

export default function Sobre() {
  return (
    <>
      <section className="bg-white border-b border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-bronze">
              A Wellpec
            </div>
            <h1 className="mt-4 font-display font-black text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Especialistas no fornecimento{" "}
              <span className="text-brand">para operações industriais críticas</span>.
            </h1>
            <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-2xl">
              A Wellpec nasceu da necessidade de levar precisão, agilidade e
              confiança para a cadeia de suprimentos das indústrias de óleo, gás
              e energia. Combinamos conhecimento técnico, rede de fornecedores
              qualificados e processos comerciais enxutos para sustentar o
              desempenho de operações em que o tempo é dinheiro.
            </p>
          </div>
          <div className="lg:col-span-5 rounded-sm overflow-hidden border border-surface-line">
            <img
              src="/images/brand/wellpec-feira.jpeg"
              alt="Equipe corporativa B2B Wellpec"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-surface-alt border-y border-surface-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            eyebrow="Nosso posicionamento"
            title="Missão, visão e valores."
            subtitle="Princípios que guiam cada cotação, cada contrato e cada entrega."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: "Missão",
                text:
                  "Fornecer soluções, materiais consumíveis e spare parts que garantam continuidade, segurança e performance às operações industriais dos nossos clientes.",
              },
              {
                icon: Eye,
                title: "Visão",
                text:
                  "Ser referência em fornecimento industrial especializado no Brasil e na América Latina, reconhecida pela confiabilidade técnica e excelência comercial.",
              },
              {
                icon: Award,
                title: "Valores",
                text:
                  "Compromisso com qualidade, transparência, atendimento próximo, rigor técnico e respeito aos prazos e às pessoas.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`value-${title}`}
                className="p-8 bg-white border border-surface-line rounded-sm hover:border-bronze/40 transition-colors"
              >
                <div className="h-12 w-12 rounded-sm bg-brand text-white flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display font-bold text-ink text-xl">
                  {title}
                </h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Compromisso com qualidade"
              title="Rigor técnico como padrão de operação."
              subtitle="Trabalhamos com fornecedores auditados, documentação completa e rastreabilidade em cada lote. Nossa especificação é conduzida por engenharia de aplicação, reduzindo erros, retrabalho e downtime."
            />
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                title: "Especificação correta",
                text:
                  "Análise conjunta entre cliente e time técnico antes de cada proposta.",
              },
              {
                title: "Rastreabilidade total",
                text:
                  "Certificados, lotes e relatórios disponíveis sob demanda.",
              },
              {
                title: "Prazos confiáveis",
                text:
                  "Acompanhamento ativo do pedido e comunicação proativa de status.",
              },
              {
                title: "Relacionamento de longo prazo",
                text:
                  "Contratos de fornecimento recorrente com previsibilidade e SLA.",
              },
            ].map((b) => (
              <div
                key={b.title}
                className="p-6 border border-surface-line rounded-sm"
              >
                <Compass className="h-5 w-5 text-bronze" />
                <h4 className="mt-3 font-semibold text-ink">{b.title}</h4>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                  {b.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-brand-noise text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h3 className="font-display font-bold text-3xl sm:text-4xl leading-tight tracking-tight">
            Converse com o time comercial da Wellpec.
          </h3>
          <p className="mt-4 text-white/80">
            Entenda como podemos ser uma extensão da sua operação de suprimentos.
          </p>
          <Link
            to="/contato"
            data-testid="sobre-cta"
            className="mt-8 inline-flex items-center gap-2 bg-white text-brand font-semibold px-7 py-4 rounded-sm hover:bg-surface-alt transition-colors"
          >
            Falar com a Wellpec <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
